<script setup lang="ts">
import { ref, watch } from 'vue';
import Icon from '@spectee/components/Atoms/IconBase/index.vue';
defineExpose({
  name: 'ActionMenu',
});

interface ActionMenuItem {
  text: string;
  iconName?: string;
  isDelete?: boolean;
  onClick?: () => void;
}

interface Props {
  items: Array<ActionMenuItem>;
  visible?: boolean;
}

const props = withDefaults(defineProps<Props>(), {});
const visible = ref(props.visible);
const emit = defineEmits(['update:visible', 'onClickMenu']);
watch(
  () => props.visible,
  newValue => {
    visible.value = newValue;
  }
);

// Watch for changes in visible and emit them to the parent
watch(visible, newValue => {
  emit('update:visible', newValue);
});

const handleMenuClick = () => {
  emit('onClickMenu');
};
</script>

<template>
  <div class="ActionMenu">
    <el-popover
      trigger="click"
      ref="popoverRef"
      placement="bottom-end"
      v-model:visible="visible"
    >
      <template #default>
        <ul class="ActionMenu__menu">
          <li v-for="item in items">
            <button
              class="ActionMenu__menuButton"
              :class="[
                {
                  'ActionMenu__menuButton--icon': item.iconName,
                  'ActionMenu__menuButton--delete': item.isDelete,
                },
              ]"
              @click="item.onClick"
            >
              <Icon
                class="ActionMenu__menuIcon"
                v-if="item.iconName || item.isDelete"
                :iconName="
                  item.isDelete ? 'Delete' : item.iconName ? item.iconName : ''
                "
              />
              <span class="ActionMenu__menuLabel">{{ item.text }}</span>
            </button>
          </li>
        </ul>
      </template>
      <template #reference>
        <el-button
          class="ActionMenu__button"
          aria-label="ActionMenu"
          @click="handleMenuClick"
        >
          <Icon class="ActionMenu__icon" iconName="More" />
        </el-button>
      </template>
    </el-popover>
  </div>
</template>

<style lang="scss" scoped>
@use '@spectee/assets/scss/_forward.scss' as *;

.ActionMenu {
  position: sticky;
  & &__button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: $service-unit * 11;
    height: $service-unit * 10;
    color: $color-gray-80;
    background-color: transparent;
    border: 0;

    &:focus,
    &:focus-visible {
      color: inherit;
      background-color: $color-gray-20;
      border-radius: 0;
      box-shadow: none;
    }

    &:focus-visible {
      outline: 0;
    }
  }

  &__menuButton {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: $service-unit * 8;
    padding: 0 $service-unit * 3;
    cursor: pointer;
    background-color: transparent;

    &:hover {
      background-color: $color-gray-20;
    }

    &--edit {
      color: $color-gray-70;
    }

    &--delete {
      color: $color-red-80;
    }
  }

  &__menuLabel {
    width: 100%;
    margin-left: $service-unit;
    font-size: $service-font-size-mp;
    text-align: left;
  }

  & &__icon {
    width: $service-unit * 5;
    height: $service-unit * 5;
  }
}

.ActionMenu__menuIcon {
  flex-shrink: 0;
  width: $service-unit * 6;
  height: $service-unit * 6;
}
</style>
